import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, CreditCard } from "lucide-react";
import { listPendingOrders, PENDING_EVENT } from "../lib/pendingOrders";
import { money } from "../lib/format";

/** An order exists upstream but has not been paid.
 *
 *  Order creation happens before the payment hand-off, so a customer whose
 *  payment failed, was abandoned, or completed in another tab still HAS a
 *  reservation. This makes that visible and resumable instead of leaving them to
 *  rebuild a cart for a booking they already hold.
 */
export const PendingPaymentBanner = ({ className = "" }) => {
  const [orders, setOrders] = useState(listPendingOrders);

  useEffect(() => {
    const sync = () => setOrders(listPendingOrders());
    window.addEventListener(PENDING_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(PENDING_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  if (orders.length === 0) return null;

  return (
    <div data-testid="pending-payment-banner" className={`space-y-3 ${className}`}>
      {orders.map((o) => (
        <div
          key={o.code}
          data-testid={`pending-order-${o.code}`}
          role="status"
          className="flex flex-col gap-3 rounded-2xl border border-ember/40 bg-ember/[0.07] p-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex items-start gap-3">
            <Clock className="mt-0.5 h-5 w-5 shrink-0 text-ember" aria-hidden="true" />
            <div className="text-sm text-navy">
              <p className="font-bold">
                You have a reservation waiting for payment — order{" "}
                <code data-testid={`pending-order-code-${o.code}`} className="font-bold">{o.code}</code>
              </p>
              <p className="mt-0.5 text-slate-700">
                {typeof o.payTodayAmount === "number" && o.payTodayAmount > 0 ? (
                  <>
                    <strong data-testid={`pending-order-amount-${o.code}`} className="font-bold">
                      {money(o.payTodayAmount)}
                    </strong>{" "}
                    is due to secure it. Your boats are held until then.
                  </>
                ) : (
                  <>Your boats are held until payment is completed.</>
                )}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Link
              to={`/booking/payment?code=${encodeURIComponent(o.code)}`}
              data-testid={`pending-order-resume-${o.code}`}
              className="inline-flex items-center gap-2 rounded-full bg-ember px-5 py-2.5 text-xs font-bold text-white transition-colors hover:bg-ember-hover"
            >
              <CreditCard className="h-3.5 w-3.5" aria-hidden="true" />
              Resume payment
            </Link>
            <Link
              to={`/booking/confirmation?code=${encodeURIComponent(o.code)}`}
              data-testid={`pending-order-view-${o.code}`}
              className="inline-flex items-center rounded-full border border-navy/20 px-5 py-2.5 text-xs font-bold text-navy transition-colors hover:bg-white"
            >
              View reservation
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PendingPaymentBanner;
